import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import initUserAnalyticsCookie from '@src/quince-site-common/src/utils/init-user-analytics-cookie'
import setCookie from '@src/quince-site-common/src/utils/setCookie'

import EXPERIMENT_CONFIG_LIST from '../configs/experiments.configList'
import { setAssignedExperimentBuckets } from '../configs/experiments.manager'
import { AssignedExperimentBuckets, ExperimentConfiguration } from '../experiments.types'

import { generateCookieStringForExperimentBucket, getExperimentDetailsFromCookieString } from './experiments.cookie'
import generateExperimentBucketForUser from './experiments.generateUserBucket'
import { getQueryParamOverrides } from './experiments.queryParam'

type ExperimentConfigurationMap = Record<string, ExperimentConfiguration>

const CACHE_BUCKET_DAYS = 60
const UTM_ENFORCE_BUCKET_URL_PARAM = 'utm_exp_force_bucket'
const COOKIE_AB_EXPERIMENT_BUCKETS = '_exp_cached_bucket'
const COOKIE_AB_EXPERIMENT_BUCKETS_ACTIVE = '_exp_cached_bucket_active'

const experimentByName = EXPERIMENT_CONFIG_LIST.reduce<ExperimentConfigurationMap>(
  (map, experimentConfiguration: ExperimentConfiguration) => {
    map[experimentConfiguration.name] = experimentConfiguration
    return map
  },
  {},
)

export const isExperimentCurrentlyActive = (experiment: ExperimentConfiguration) => {
  // it is inactive if any of the buckets are at 100 and the rest are at 0
  const buckets = experiment.buckets
  const inactiveBuckets = buckets.filter(bucket => bucket.ratio === 100)
  return inactiveBuckets.length === 0
}

const getCookieExpiry = () => String(new Date().getTime() + CACHE_BUCKET_DAYS * 24 * 3600 * 1000)

const initialize = (assignedExperimentBucketConfigForUser: AssignedExperimentBuckets) => {
  const userId = getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getlbUserId()
  if (userId) {
    for (const experiment of EXPERIMENT_CONFIG_LIST) {
      const userBucket = generateExperimentBucketForUser(experiment, userId)
      if (userBucket) {
        assignedExperimentBucketConfigForUser[experiment.name] = {
          // expCachedBucketData
          name: experiment.name,
          date: experiment.date,
          bucket: userBucket,
          expiryTime: getCookieExpiry(),
          loggedInUsersOnly: !!experiment.loggedInUsersOnly,
          isActive: isExperimentCurrentlyActive(experiment),
        }
      }
    }
  }
}

const overrideBucketsFromCookie = (assignedExperimentBucketConfigForUser: AssignedExperimentBuckets) => {
  const expCachedBucketCookieString = getCookie(COOKIE_AB_EXPERIMENT_BUCKETS)
  if (expCachedBucketCookieString) {
    const expBucketStringList = expCachedBucketCookieString.split('___')

    expBucketStringList.forEach((expBucketString: string) => {
      const { expName, expBucket, expDate, expiryTime } = getExperimentDetailsFromCookieString(expBucketString)
      /**
       * Use cookie cached bucket if:
       * 1. Cookie cached bucket not expired
       * 2. Experiment has not been updated (represented by date in experiment config)
       */
      if (
        expiryTime &&
        expBucket &&
        expDate &&
        expDate === experimentByName[expName]?.date &&
        parseInt(expiryTime) > new Date().getTime()
      ) {
        assignedExperimentBucketConfigForUser[expName] = {
          name: expName,
          bucket: expBucket,
          date: expDate,
          expiryTime,
          loggedInUsersOnly: !!assignedExperimentBucketConfigForUser[expName]?.loggedInUsersOnly,
          isActive: isExperimentCurrentlyActive(experimentByName[expName]),
        }
      }
    })
  }
}

const overrideBucketsFromQueryParam = (assignedExperimentBucketConfigForUser: AssignedExperimentBuckets) => {
  const windowUrl = new URL(window.location.href)
  const experimentOverrides = windowUrl.searchParams.getAll(UTM_ENFORCE_BUCKET_URL_PARAM)
  experimentOverrides.forEach(experimentBucketString => {
    const queryParamOverrides = getQueryParamOverrides(experimentBucketString)
    const { expBucket, expName } = queryParamOverrides
    const experiment = experimentByName[expName]

    if (experiment) {
      assignedExperimentBucketConfigForUser[expName] = {
        name: expName,
        bucket: expBucket,
        date: experiment.date,
        expiryTime: getCookieExpiry(),
        loggedInUsersOnly: !!assignedExperimentBucketConfigForUser[expName]?.loggedInUsersOnly,
        isActive: isExperimentCurrentlyActive(experiment),
      }
    }
  })
}

const setBucketsToCookie = (assignedExperimentBucketConfigForUser: AssignedExperimentBuckets) => {
  // Set assignedExperimentBucketConfigForUser to cookie
  const isSecure = true
  const expCachedBucketData = Object.values(assignedExperimentBucketConfigForUser)
  const activeExperimentBuckets = expCachedBucketData.filter(bucket => bucket.isActive)
  const cookieString = generateCookieStringForExperimentBucket(expCachedBucketData)
  const activeCookieString = generateCookieStringForExperimentBucket(activeExperimentBuckets)
  setCookie(COOKIE_AB_EXPERIMENT_BUCKETS, cookieString, CACHE_BUCKET_DAYS * 24 * 60, isSecure)
  setCookie(COOKIE_AB_EXPERIMENT_BUCKETS_ACTIVE, activeCookieString, CACHE_BUCKET_DAYS * 24 * 60, isSecure)
}

const initializePipeline = [
  initUserAnalyticsCookie,
  initialize,
  overrideBucketsFromCookie,
  overrideBucketsFromQueryParam,
  setBucketsToCookie,
]

export const initializeExperimentManager = () => {
  const assignedBucketConfig = {} as AssignedExperimentBuckets
  initializePipeline.forEach(pipelineFunction => {
    pipelineFunction(assignedBucketConfig)
  })

  setAssignedExperimentBuckets(assignedBucketConfig)
}
